/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState } from 'react'
import { useNavigate } from 'react-router'
import { useServiceContext } from '../../../root'
import type { FolderStructureTemplateType } from '../../../services/api/api.folderstructuretemplatetypes'
import ActionsMenu from '../../../components/shared/ActionsMenu'
import ModalRemove from './Actions/ModalRemove'

interface TableMenuProps {
  item: FolderStructureTemplateType
  onEditFolderStrctTplType: (folderStructureTemplateType: FolderStructureTemplateType) => void
  onDeleteSuccess?: () => void
  onExportStart?: () => void
  onExportEnd?: () => void
}

const TableMenu: React.FC<TableMenuProps> = ({
  item,
  onEditFolderStrctTplType,
  onDeleteSuccess,
  onExportStart,
  onExportEnd
}) => {
  const context = useServiceContext()
  const navigate = useNavigate()
  const { translate } = context.service.filter
  const { toast } = context.service
  const [showRemoveModal, setShowRemoveModal] = useState(false)

  const handleEdit = () => {
    onEditFolderStrctTplType(item)
  }

  const handleRemove = () => {
    setShowRemoveModal(true)
  }

  const handleExport = async () => {
    try {
      // Start loading
      if (onExportStart) {
        onExportStart()
      }

      // Export single folder structure template type with entityId parameter
      await context.api.settings.exportSettingsData('FOLDER_STRUCTURE_TYPES', undefined, item.id, translate)
    } catch (error) {
      toast.error(translate('SETTINGS_IMPORT_FAIL'))
    } finally {
      // End loading
      if (onExportEnd) {
        onExportEnd()
      }
    }
  }

  const handleActivity = () => {
    // Navigate to settings activity page with folder structure template type parameters
    // This keeps the user in the settings context (folder structure template types tab stays active)
    const params = new URLSearchParams({
      id: item.id.toString(),
      type: 'FST_TYPE',
      itemName: item.name
    })
    navigate(`/settings/activity?${params.toString()}`)
  }

  const settings = {
    menuId: `action-menu-folder-structure-template-type-${item.id}`,
    items: [] as Array<{
      value: string
      action: () => void
    }>
  }

  // Only show edit and remove for non-system items
  if (!item.system) {
    settings.items.push(
      {
        value: 'ACTION_EDIT',
        action: handleEdit
      },
      {
        value: 'ACTION_REMOVE',
        action: handleRemove
      }
    )
  }

  // Export and Activity are available for all items
  settings.items.push(
    {
      value: 'ACTION_EXPORT',
      action: handleExport
    },
    {
      value: 'BROWSER_ACTION_ACTIVITY',
      action: handleActivity
    }
  )

  return (
    <>
      <ActionsMenu {...settings} />
      {showRemoveModal && (
        <ModalRemove
          folderStrctTplType={item}
          onClose={() => setShowRemoveModal(false)}
          onSuccess={() => {
            setShowRemoveModal(false)
            if (onDeleteSuccess) {
              onDeleteSuccess()
            }
          }}
        />
      )}
    </>
  )
}

export default TableMenu
